import React, {useEffect, useRef, useState} from 'react';

// Small hand-drawn SVG charts for the Kennzahlen tab. Written here rather than
// pulled in as a package: the tab needs three simple shapes, and a charting
// library would be the heaviest dependency in the bundle for it.

const GRID = '#efe9e5';
const AXIS = '#cfc6c0';
const MUTED = '#8a827f';
const INK = '#57504e';

let gradientSeq = 0;

// Charts are drawn in real pixels so lines keep their width; this follows the
// container as the admin column resizes.
function useWidth() {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        const measure = () => setWidth(node.clientWidth);
        measure();
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(measure);
            observer.observe(node);
            return () => observer.disconnect();
        }
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);
    return [ref, width];
}

function useGradientId() {
    const id = useRef(null);
    if (id.current === null) {
        gradientSeq += 1;
        id.current = `kz-grad-${gradientSeq}`;
    }
    return id.current;
}

// A round top for the y axis, so the gridlines land on readable numbers. Never
// below 4: everything charted is a count, and a smaller top would put the
// gridlines on fractions like 0,6 of a signup.
function niceMax(value) {
    const v = Math.max(value, 4);
    const p = Math.pow(10, Math.floor(Math.log10(v)));
    for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
    return 10 * p;
}

function ticksFor(max) {
    const count = max % 4 === 0 ? 4 : max % 5 === 0 ? 5 : 4;
    return Array.from({length: count + 1}, (_, i) => (max / count) * i);
}

const formatTick = (v, format) => (format ? format(v) : (
    Number.isInteger(v) ? v.toLocaleString('de-DE') : v.toLocaleString('de-DE', {maximumFractionDigits: 1})
));

// Monotone cubic interpolation: smooth, but never swings below zero or above
// a peak the data does not have.
function smoothPath(points) {
    const n = points.length;
    if (n === 0) return '';
    if (n === 1) return `M${points[0][0]},${points[0][1]}`;
    const dx = [], slope = [], tangent = [];
    for (let i = 0; i < n - 1; i++) {
        dx[i] = points[i + 1][0] - points[i][0];
        slope[i] = (points[i + 1][1] - points[i][1]) / dx[i];
    }
    tangent[0] = slope[0];
    tangent[n - 1] = slope[n - 2];
    for (let i = 1; i < n - 1; i++) {
        tangent[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2;
    }
    for (let i = 0; i < n - 1; i++) {
        if (slope[i] === 0) { tangent[i] = 0; tangent[i + 1] = 0; continue; }
        const a = tangent[i] / slope[i], b = tangent[i + 1] / slope[i], h = a * a + b * b;
        if (h > 9) {
            const k = 3 / Math.sqrt(h);
            tangent[i] = k * a * slope[i];
            tangent[i + 1] = k * b * slope[i];
        }
    }
    let d = `M${points[0][0]},${points[0][1]}`;
    for (let i = 0; i < n - 1; i++) {
        const h = dx[i] / 3;
        d += ` C${points[i][0] + h},${points[i][1] + tangent[i] * h}`
           + ` ${points[i + 1][0] - h},${points[i + 1][1] - tangent[i + 1] * h}`
           + ` ${points[i + 1][0]},${points[i + 1][1]}`;
    }
    return d;
}

// Axis and gridlines shared by the line and bar charts.
function Frame({width, height, pad, max, labels, format}) {
    const plotW = width - pad.l - pad.r;
    const slot = plotW / labels.length;
    const every = Math.max(1, Math.ceil(labels.length / Math.max(1, Math.floor(plotW / 64))));
    return (<>
        {ticksFor(max).map((v, i) => {
            const y = height - pad.b - ((height - pad.t - pad.b) * v) / max;
            return (
                <g key={i}>
                    <line x1={pad.l} x2={width - pad.r} y1={y} y2={y}
                        stroke={i ? GRID : AXIS} strokeDasharray={i ? '3 4' : undefined} />
                    <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize="11" fill={MUTED}>
                        {formatTick(v, format)}
                    </text>
                </g>
            );
        })}
        {labels.map((label, i) => {
            const last = i === labels.length - 1;
            if (!last && (i % every !== 0 || labels.length - 1 - i < every / 2)) return null;
            return (
                <text key={i} x={pad.l + slot * i + slot / 2} y={height - pad.b + 16}
                    textAnchor="middle" fontSize="11" fill={MUTED}>{label}</text>
            );
        })}
    </>);
}

function Tooltip({x, width, title, rows}) {
    // Flip to the left of the cursor near the right edge so it never overflows.
    const left = x > width - 190 ? x - 180 : x + 14;
    return (
        <div className="kz-tip" style={{left}}>
            <b>{title}</b>
            {rows.map(r => (
                <div key={r.name}><i style={{background: r.color}} />{r.name}<span>{r.value}</span></div>
            ))}
        </div>
    );
}

// Hover lookup: which bucket is under the pointer.
function useHover(pad, width, count) {
    const [index, setIndex] = useState(null);
    const onMove = (e) => {
        const box = e.currentTarget.getBoundingClientRect();
        const slot = (width - pad.l - pad.r) / count;
        const i = Math.floor((e.clientX - box.left - pad.l) / slot);
        setIndex(i >= 0 && i < count ? i : null);
    };
    return [index, onMove, () => setIndex(null)];
}

export function LineChart({labels, series, height = 230, area = false, directLabels = true, format}) {
    const [ref, width] = useWidth();
    const gradientId = useGradientId();
    const pad = {t: 12, r: directLabels ? 96 : 14, b: 26, l: 40};
    const [hover, onMove, onLeave] = useHover(pad, width, labels.length);
    const max = niceMax(Math.max(0, ...series.flatMap(s => s.data)) * 1.1);
    const slot = (width - pad.l - pad.r) / Math.max(1, labels.length);
    const X = i => pad.l + slot * i + slot / 2;
    const Y = v => height - pad.b - ((height - pad.t - pad.b) * v) / max;
    const last = labels.length - 1;

    // End labels are pushed apart when the lines finish close together, so
    // "Anmeldungen 0" and "Profile 0" never print on top of each other.
    const labelY = {};
    series
        .map(s => ({name: s.name, y: Y(s.data[last]) + 4}))
        .sort((a, b) => b.y - a.y)
        .forEach((l, i, all) => {
            // Upwards, bottom label first: pushing down would run into the dates.
            const prev = i > 0 ? labelY[all[i - 1].name] : Infinity;
            labelY[l.name] = Math.min(l.y, prev - 14);
        });

    return (
        <div ref={ref} className="kz-chart" style={{height}}>
            {width > 0 && labels.length > 0 && (
                <svg width={width} height={height} onMouseMove={onMove} onMouseLeave={onLeave}>
                    <Frame width={width} height={height} pad={pad} max={max} labels={labels} format={format} />
                    {series.map((s, k) => {
                        const d = smoothPath(s.data.map((v, i) => [X(i), Y(v)]));
                        return (
                            <g key={s.name}>
                                {area && (<>
                                    <defs>
                                        <linearGradient id={`${gradientId}-${k}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={s.color} stopOpacity="0.28" />
                                            <stop offset="100%" stopColor={s.color} stopOpacity="0.02" />
                                        </linearGradient>
                                    </defs>
                                    <path d={`${d} L${X(last)},${Y(0)} L${X(0)},${Y(0)} Z`}
                                        fill={`url(#${gradientId}-${k})`} />
                                </>)}
                                <path d={d} fill="none" stroke={s.color} strokeWidth="2.5"
                                    strokeLinejoin="round" strokeLinecap="round" />
                                <circle cx={X(last)} cy={Y(s.data[last])} r="4"
                                    fill={s.color} stroke="#fff" strokeWidth="2" />
                                {directLabels && (
                                    <text x={X(last) + 10} y={labelY[s.name]} fontSize="11" fill={INK}>
                                        {`${s.name} ${s.data[last]}`}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                    {hover !== null && (<>
                        <line x1={X(hover)} x2={X(hover)} y1={pad.t} y2={height - pad.b}
                            stroke={AXIS} strokeDasharray="3 3" />
                        {series.map(s => (
                            <circle key={s.name} cx={X(hover)} cy={Y(s.data[hover])} r="4"
                                fill={s.color} stroke="#fff" strokeWidth="2" />
                        ))}
                    </>)}
                </svg>
            )}
            {hover !== null && (
                <Tooltip x={X(hover)} width={width} title={labels[hover]}
                    rows={series.map(s => ({name: s.name, color: s.color,
                                            value: format ? format(s.data[hover]) : s.data[hover]}))} />
            )}
        </div>
    );
}

export function StackedBars({labels, series, height = 200}) {
    const [ref, width] = useWidth();
    const pad = {t: 12, r: 24, b: 26, l: 40};
    const [hover, onMove, onLeave] = useHover(pad, width, labels.length);
    const totals = labels.map((_, i) => series.reduce((sum, s) => sum + s.data[i], 0));
    const max = niceMax(Math.max(0, ...totals) * 1.1);
    const slot = (width - pad.l - pad.r) / Math.max(1, labels.length);
    const barW = Math.max(3, Math.min(28, slot * 0.62));
    const plotH = height - pad.t - pad.b;

    return (
        <div ref={ref} className="kz-chart" style={{height}}>
            {width > 0 && labels.length > 0 && (
                <svg width={width} height={height} onMouseMove={onMove} onMouseLeave={onLeave}>
                    <Frame width={width} height={height} pad={pad} max={max} labels={labels} />
                    {labels.map((_, i) => {
                        let acc = 0;
                        const x = pad.l + slot * i + (slot - barW) / 2;
                        const topIndex = series.map(s => s.data[i] > 0).lastIndexOf(true);
                        return (
                            <g key={i} opacity={hover === null || hover === i ? 1 : 0.55}>
                                {series.map((s, k) => {
                                    const v = s.data[i];
                                    if (!v) return null;
                                    const y0 = height - pad.b - (plotH * acc) / max;
                                    const y1 = height - pad.b - (plotH * (acc + v)) / max;
                                    // A 2px gap between stacked segments keeps them apart.
                                    const h = Math.max(1, y0 - y1 - (acc ? 2 : 0));
                                    acc += v;
                                    return <rect key={k} x={x} y={y1} width={barW} height={h}
                                        rx={k === topIndex ? 3 : 0} fill={s.color} />;
                                })}
                            </g>
                        );
                    })}
                </svg>
            )}
            {hover !== null && (
                <Tooltip x={pad.l + slot * hover + slot / 2} width={width} title={labels[hover]}
                    rows={series.map(s => ({name: s.name, color: s.color, value: s.data[hover]}))} />
            )}
        </div>
    );
}

export function Donut({items, size = 160, thickness = 16, center, sub}) {
    const r = size / 2 - 4, inner = r - thickness, c = size / 2;
    const total = items.reduce((sum, it) => sum + it.value, 0);
    const point = (radius, angle) => `${c + radius * Math.cos(angle)} ${c + radius * Math.sin(angle)}`;
    let start = -Math.PI / 2;
    return (
        <svg width={size} height={size} className="kz-donut">
            {total === 0 ? (
                <circle cx={c} cy={c} r={r - thickness / 2} fill="none" stroke={GRID} strokeWidth={thickness} />
            ) : items.filter(it => it.value > 0).map((it, i, shown) => {
                // A single slice is a full ring; an SVG arc cannot draw 360°.
                if (shown.length === 1) {
                    return <circle key={i} cx={c} cy={c} r={r - thickness / 2} fill="none"
                        stroke={it.color} strokeWidth={thickness} />;
                }
                const end = start + (2 * Math.PI * it.value) / total;
                const large = end - start > Math.PI ? 1 : 0;
                const d = `M ${point(r, start)} A ${r} ${r} 0 ${large} 1 ${point(r, end)}`
                        + ` L ${point(inner, end)} A ${inner} ${inner} 0 ${large} 0 ${point(inner, start)} Z`;
                start = end;
                return <path key={i} d={d} fill={it.color} stroke="#fff" strokeWidth="2" />;
            })}
            <text x={c} y={c + 4} textAnchor="middle" fontSize="24" fontWeight="700" fill="#1f1a19">{center}</text>
            {sub && <text x={c} y={c + 22} textAnchor="middle" fontSize="11" fill={MUTED}>{sub}</text>}
        </svg>
    );
}
